import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
const supabaseServiceRoleKey = import.meta.env?.VITE_SUPABASE_SERVICE_ROLE_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

const PRODUCTION_DOMAIN = 'idartapps.com';
const isDevelopment = import.meta.env?.DEV;
const isProduction = 
  window.location?.hostname === PRODUCTION_DOMAIN ||
  window.location?.hostname === 'idartists3878.builtwithrocket.new' ||
  window.location?.hostname?.endsWith('.builtwithrocket.new');

const supabaseConfig = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window?.localStorage,
    storageKey: isProduction ? 'idartists.admin.auth.token' : 'supabase.auth.token',
    flowType: 'pkce',
    ...(isProduction && {
      redirectTo: `https://${PRODUCTION_DOMAIN}/auth/callback`,
    })
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: { 
      'x-client-info': `idartists-admin-panel@${isProduction ? 'production' : 'development'}`,
      'x-deployment-domain': window.location?.hostname,
    },
  },
  realtime: {
    params: {
      eventsPerSecond: isProduction ? 10 : 100
    }
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, supabaseConfig);

export let supabaseAdmin = null;

const isValidServiceRoleKey = (key) => {
  if (!key) return false;
  
  const placeholders = [
    'your-service-role-key-here',
    'your-actual-service-role-key-here', 
    'INSERT_YOUR_SERVICE_ROLE_KEY',
    'sk-placeholder',
    'service-role-key-placeholder'
  ];
  
  if (placeholders?.includes(key)) return false;
  if (key?.length < 100) return false;
  if (key === supabaseAnonKey) return false;
  
  return true;
};

if (isValidServiceRoleKey(supabaseServiceRoleKey)) {
  try {
    supabaseAdmin = createClient(supabaseUrl, supabaseServiceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false,
        storage: null,
        storageKey: isProduction ? 'idartists.admin.service.token' : 'supabase.auth.admin.token',
      },
      db: {
        schema: 'public',
      },
      global: {
        headers: {
          Authorization: `Bearer ${supabaseServiceRoleKey}`,
          'x-client-info': `idartists-admin-service@${isProduction ? 'production' : 'development'}`,
        }
      }
    });
  } catch (error) {
    console.warn('Failed to initialize admin client:', error);
    supabaseAdmin = null;
  }
} else {
  const missingKeyReason = !supabaseServiceRoleKey 
    ? 'VITE_SUPABASE_SERVICE_ROLE_KEY environment variable not found'
    : 'VITE_SUPABASE_SERVICE_ROLE_KEY appears to be a placeholder value';
    
  console.warn(`⚠️ Admin operations not available: ${missingKeyReason}`);
}

export const isAdminConfigured = () => {
  return !!(supabaseAdmin && isValidServiceRoleKey(supabaseServiceRoleKey));
};

export const getAdminClient = () => {
  if (!supabaseAdmin) {
    const errorDetails = isProduction 
      ? 'Admin operations require proper service role key configuration. Please contact your system administrator.'
      : `Admin operations are not configured. 

**Configuration Required:**
1. Get your Service Role Key from Supabase Dashboard → Settings → API
2. Add to environment: VITE_SUPABASE_SERVICE_ROLE_KEY=your-actual-key-here
3. Restart the application

**Current Issue:** ${!supabaseServiceRoleKey 
  ? 'Environment variable VITE_SUPABASE_SERVICE_ROLE_KEY is missing'
  : 'Service role key appears to be a placeholder or invalid'}`;

    throw new Error(errorDetails);
  }
  return supabaseAdmin;
};

export const handleAdminError = (error, operation = 'admin operation') => {
  console.error(`${operation} failed:`, error);
  
  if (error?.message?.includes('Invalid API key') || error?.code === 'invalid_api_key') {
    const keyIssue = !isValidServiceRoleKey(supabaseServiceRoleKey)
      ? 'The service role key is missing or appears to be a placeholder' :'The service role key may be invalid or expired';

    const errorMessage = isProduction 
      ? `❌ API Key Issue for ${operation}: ${keyIssue}. Contact your system administrator.`
      : `❌ Invalid API Key for ${operation}

**Issue:** ${keyIssue}

**Quick Fix:**
1. Go to your Supabase Dashboard → Settings → API
2. Copy your **Service Role Key** (NOT the anon public key)
3. Update your .env file: VITE_SUPABASE_SERVICE_ROLE_KEY=your-actual-service-role-key-here
4. Restart your development server`;
    
    throw new Error(errorMessage);
  }
  
  if (error?.message?.includes('service_role') || 
      error?.message?.includes('insufficient_permissions') ||
      error?.code === 'insufficient_permissions') {
    throw new Error(`Admin ${operation} requires proper service role configuration.`);
  }
  
  throw error;
};

export const refreshSession = async () => {
  try {
    const { data: currentSession, error: sessionError } = await supabase?.auth?.getSession();
    
    if (sessionError && sessionError?.message?.includes('refresh')) {
      console.warn('Invalid session detected during refresh check:', sessionError?.message);
      await clearAuthData();
      return { data: null, error: sessionError };
    }

    if (!currentSession?.session) {
      return { data: null, error: { message: 'No active session' } };
    }

    const { data, error } = await supabase?.auth?.refreshSession();
    
    if (error) {
      console.warn('Session refresh failed:', error?.message);
      
      if (error?.message?.includes('refresh') || 
          error?.message?.includes('token') ||
          error?.message?.includes('Invalid refresh token') ||
          error?.message?.includes('Refresh Token Not Found')) {
        
        await clearAuthData();
        return { data: null, error: { message: 'Session expired. Please log in again.' } };
      }
      
      return { data: null, error };
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Session refresh error:', error);
    
    if (error?.message?.includes('refresh') || 
        error?.message?.includes('token') ||
        error?.name === 'AuthSessionMissingError') {
      
      await clearAuthData();
      return { data: null, error: { message: 'Authentication error. Please log in again.' } };
    }
    
    return { data: null, error };
  }
};

export const clearAuthData = async () => {
  try {
    const storageKeys = [
      'idartists.admin.auth.token',
      'supabase.auth.token', 
      'idartists.admin.service.token',
      'supabase.auth.admin.token',
      'sb-localhost-auth-token',
      'sb-' + window.location?.hostname?.replace(/\./g, '-') + '-auth-token',
    ];
    
    storageKeys?.forEach(key => {
      localStorage?.removeItem(key);
      sessionStorage?.removeItem(key);
    });
    
    const { error: signOutError } = await supabase?.auth?.signOut();
    
    if (signOutError) {
      console.warn('Error during signOut:', signOutError?.message);
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error clearing auth data:', error);
    
    try {
      localStorage?.clear?.();
      sessionStorage?.clear?.();
    } catch (storageError) {
      console.warn('Could not clear storage:', storageError);
    }
    
    return { success: false, error };
  }
};

export const validateAndRecoverSession = async () => {
  try {
    const { data: session, error } = await supabase?.auth?.getSession();
    
    if (error) {
      if (error?.message?.includes('refresh') || 
          error?.message?.includes('token') ||
          error?.message?.includes('Invalid') ||
          error?.name === 'AuthSessionMissingError') {
        
        console.warn('Session validation failed - invalid tokens detected:', error?.message);
        await clearAuthData();
        return { isValid: false, cleared: true, error: 'Session invalid - cleared auth data' };
      }
      
      return { isValid: false, cleared: false, error: error?.message };
    }
    
    if (!session?.session) {
      return { isValid: false, cleared: false, error: 'No active session' };
    }
    
    const now = Date.now() / 1000;
    const expiresAt = session?.session?.expires_at;
    
    if (expiresAt && now > expiresAt) {
      const refreshResult = await refreshSession();
      
      if (refreshResult?.error) {
        return { isValid: false, cleared: true, error: 'Session expired and refresh failed' };
      }
      
      return { isValid: true, cleared: false, refreshed: true };
    }
    
    return { isValid: true, cleared: false };
    
  } catch (error) {
    console.error('Session validation error:', error);
    
    if (error?.message?.includes('refresh') || 
        error?.message?.includes('token') ||
        error?.name === 'AuthSessionMissingError') {
      
      await clearAuthData();
      return { isValid: false, cleared: true, error: 'Session validation failed - cleared corrupted data' };
    }
    
    return { isValid: false, cleared: false, error: error?.message };
  }
};

supabase?.auth?.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED') {
    // Token refreshed successfully
  } else if (event === 'SIGNED_OUT') {
    const storageKey = isProduction ? 'idartists.admin.auth.token' : 'supabase.auth.token';
    localStorage?.removeItem(storageKey);
  } else if (event === 'TOKEN_REFRESH_FAILED') {
    console.warn('❌ Token refresh failed - clearing session data');
    clearAuthData()?.catch(error => {
      console.error('Failed to clear auth data after token refresh failure:', error);
    });
  }
});